import { Box } from "@mui/system";
import Tab from "@mui/material/Tab";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext, TabList } from "@mui/lab";
import PatientInfo from "../Forms/PatientInfo";
import useStyles from "../../../../styles/styles";
import React, { useEffect, useState } from "react";
import PatientAddress from "../Forms/PatientAddress";
import { configState } from "../../../../recoil/atoms";
import PatientInsurance from "../Forms/PatientInsurance";
import EditPatientAccount from "../Forms/EditPatientAccount";
import { editValidationSchema } from "../FormModel/validationSchema";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { EditUserRequest, UserApi } from "../../../../api/user-management";
import { GetPatientResponse, PatientApi } from "../../../../api/medical-operations";
import { Button, Grid, LinearProgress, Typography, CircularProgress } from "@mui/material";


const EditUser = () => {
  const classes = useStyles();
  let { patientID } = useParams();
  const config = useRecoilValue(configState);
  
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("0");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [patient, setPatient] = useState<GetPatientResponse>();
  const currentValidationSchema = editValidationSchema[parseInt(selectedTab)];  

  const getPatient = () => {
    const patient = new PatientApi(config);
    patient
      .getTenantPatientById(patientID!)
      .then((res) => {
        setIsLoading(false);
        setPatient(res.data!);        
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const editUser = (values: EditUserRequest) => {
    setLoading(true);

    if(values.Contact?.PrimaryPhone){
      const primCodeCapital = values.Contact.PrimaryPhone.CountryCode.toUpperCase();
      values.Contact.PrimaryPhone.CountryCode= primCodeCapital;
    }
    if (values.Contact?.SecondaryPhone){
      const secondCodeCapital = values.Contact.SecondaryPhone!.CountryCode.toUpperCase();
      values.Contact.SecondaryPhone!.CountryCode = secondCodeCapital;
    }

    const editUser = new UserApi(config);
    editUser
      .editUser(patient?.User?.UserID!, values)
      .then((res) => {
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (values: any, actions: any) => {
    actions.setTouched({});
    actions.setSubmitting(false);
    
    const valuesEdited: EditUserRequest = {}

    switch (selectedTab) {
      case "0":
        valuesEdited.FirstName = values?.User?.FirstName;
        valuesEdited.MiddleName = values?.User?.MiddleName;
        valuesEdited.FamilyName = values?.User?.FamilyName;
        valuesEdited.MotherName = values?.User?.MotherName;
        valuesEdited.Gender = values?.User?.Gender;
        valuesEdited.BloodType = values?.User?.BloodType;
        valuesEdited.DateOfBirth = values?.User?.DateOfBirth;
        valuesEdited.MaritalStatus = values?.User?.MaritalStatus;
        valuesEdited.Identity = {
          IdentityID: values?.User?.Identity?.IdentityID,
          IdentityImageUri: values?.User?.Identity?.IdentityImageUri,
        };
        break;
      case "1":
        valuesEdited.Address = values?.User?.Address;
        break;
      case "2":
        valuesEdited.Insurance = {
          InsuranceNb: values?.User?.Insurance?.InsuranceNb!,
          InsuranceCountry: values?.User?.Insurance?.InsuranceCountry!,
          InsuranceCompany: values?.User?.Insurance?.InsuranceCompany!,
          InsurancePolicy: values?.User?.Insurance?.InsurancePolicy!,
          InsuranceIDImageURI: values?.User?.Insurance?.InsuranceIDImageURI!,
          Active: values?.User?.Insurance?.Active!,
          IssuedDate: values?.User?.Insurance?.IssuedDate!,
          ExpiryDate: values?.User?.Insurance?.ExpiryDate!,
        };        
        break;  
      case "3":
        valuesEdited.PhotoURI = values?.User?.PhotoURI;
        valuesEdited.Contact = {
          PrimaryEmail: {
            Email: values?.User?.Contact?.PrimaryEmail?.Email!,
          },
          SecondaryEmail: values?.User?.Contact?.SecondaryEmail?.Email
            ? { Email: values?.User?.Contact?.SecondaryEmail.Email }
            : undefined,
          PrimaryPhone: {
            ...values?.User?.Contact?.PrimaryPhone!,
          },
          SecondaryPhone: values?.User?.Contact?.SecondaryPhone?.Phone
            ? {
                Phone: values?.User?.Contact?.SecondaryPhone.Phone,
                CountryCode: values?.User?.Contact?.SecondaryPhone.CountryCode,
              }
            : undefined,
        };
        break;
      default:
        break;            
    }    
    editUser(valuesEdited);
  };

  return (
    <>
      <MainCard>
        {!isLoading ? (
          <Formik
            initialValues={patient!}
            enableReinitialize={true}
            validationSchema={currentValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ dirty, isValid }) => (
              <Form>
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ bgcolor: "#fff", p: 0 }}>
                    <Typography variant="h3" gutterBottom sx={{ p: 0 }}>
                      {patient?.User?.FirstName} {patient?.User?.FamilyName}
                    </Typography>
                    <TabContext value={selectedTab}>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          display: "flex",
                          justifyContent: "center",
                        }}
                        width="100%"
                      >
                        <TabList
                          onChange={handleChangeTab}
                          textColor="secondary"
                          indicatorColor="secondary"
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                        >
                          <Tab label="Info" value="0" />
                          <Tab label="Address" value="1" />
                          <Tab label="Insurance" value="2" />
                          <Tab label="Account" value="3" />
                        </TabList>
                      </Box>

                      <TabPanel value="0">
                        <PatientInfo />
                      </TabPanel>
                      <TabPanel value="1">
                        <PatientAddress />
                      </TabPanel>
                      <TabPanel value="2">
                        <PatientInsurance />
                      </TabPanel>
                      <TabPanel value="3">
                        <EditPatientAccount />
                      </TabPanel>

                      <Grid
                        container
                        sx={{
                          pt: 8,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Grid item>
                          <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            fullWidth
                            disabled={!isValid || loading}   
                            startIcon={loading ? <CircularProgress size={20} /> : null}                         
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </TabContext>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <LinearProgress color="secondary" />
        )}
      </MainCard>
    </>
  );
};

export default EditUser;
